import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Phone, Mail, Download, ExternalLink } from 'lucide-react';

interface CTAButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  action: 'contact' | 'call' | 'email' | 'download' | 'consultation' | 'external';
  text: string;
  href?: string;
  onClick?: () => void;
  className?: string;
  trackingData?: {
    source: string;
    campaign?: string;
    service?: string;
  };
  icon?: boolean;
  fullWidth?: boolean;
}

export const CTAButton: React.FC<CTAButtonProps> = ({
  variant = 'primary',
  size = 'default',
  action,
  text,
  href,
  onClick,
  className = '',
  trackingData,
  icon = true,
  fullWidth = false
}) => {
  
  const trackClick = () => {
    // Google Analytics 4 Event Tracking
    if (typeof window !== 'undefined' && window.gtag && trackingData) {
      window.gtag('event', 'cta_click', {
        event_category: 'CTA',
        event_label: action,
        custom_parameters: {
          source: trackingData.source,
          campaign: trackingData.campaign || 'default',
          service: trackingData.service || 'general',
          cta_text: text
        }
      });
    }

    // Facebook Pixel Tracking
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: text,
        content_category: action,
        value: 1
      });
    }

    console.log('🎯 CTA CLICKED:', { action, text, trackingData });
  };

  const handleClick = () => {
    trackClick();
    
    if (onClick) {
      onClick();
    } else {
      switch (action) {
        case 'contact':
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
          break;
        case 'call':
          if (href) window.open(`tel:${href}`, '_self');
          break;
        case 'email':
          if (href) window.open(`mailto:${href}`, '_self');
          break;
        case 'external':
          if (href) window.open(href, '_blank', 'noopener,noreferrer');
          break;
        case 'download':
          if (href) {
            const link = document.createElement('a');
            link.href = href;
            link.download = '';
            link.click();
          }
          break;
        case 'consultation':
          // Redirect to consultation booking or contact form
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
          break;
      }
    }
  };

  const getIcon = () => {
    if (!icon) return null;
    
    switch (action) {
      case 'contact':
      case 'consultation':
        return <ArrowRight className="w-4 h-4 ml-2" />;
      case 'call':
        return <Phone className="w-4 h-4 ml-2" />;
      case 'email':
        return <Mail className="w-4 h-4 ml-2" />;
      case 'download':
        return <Download className="w-4 h-4 ml-2" />;
      case 'external':
        return <ExternalLink className="w-4 h-4 ml-2" />;
      default:
        return <ArrowRight className="w-4 h-4 ml-2" />;
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200';
      case 'secondary':
        return 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200';
      case 'outline':
        return 'bg-transparent text-blue-600 border-2 border-blue-600 hover:bg-blue-600 hover:text-white active:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200';
      case 'ghost':
        return 'hover:bg-blue-50 text-blue-600 transition-all duration-200';
      default:
        return '';
    }
  };

  return (
    <Button
      size={size}
      onClick={handleClick}
      className={`
        ${getVariantStyles()}
        ${fullWidth ? 'w-full' : ''}
        font-semibold
        ${className}
      `}
    >
      {text}
      {getIcon()}
    </Button>
  );
};